import React from "react";

/**
 * Two-or-more-way segmented switch with a sliding active pill.
 *
 * The pill is one absolutely-positioned element moved with `translateX` rather
 * than a background on each button: the active state then travels between
 * options instead of blinking from one to the next, which is what makes the
 * control read as a switch rather than as two buttons. Transform-only, so the
 * movement stays on the compositor.
 *
 * @param {Object} props
 * @param {Array<{id: string, label: string, Icon?: React.ComponentType<{className?: string}>, count?: number}>} props.items
 * @param {string} props.value - Active item id.
 * @param {(id: string) => void} props.onChange
 * @param {string} props.label - Accessible group name.
 * @param {string} [props.className]
 */
export function SegmentedControl({
  items,
  value,
  onChange,
  label,
  className = "",
}) {
  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.id === value)
  );

  return (
    <div
      role="group"
      aria-label={label}
      className={`relative flex rounded-full bg-gray-100 p-1 ${className}`}
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-1 left-1 rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform duration-200 ease-out motion-reduce:transition-none"
        style={{
          width: `calc((100% - 0.5rem) / ${items.length})`,
          transform: `translateX(${activeIndex * 100}%)`,
        }}
      />

      {items.map((item) => {
        const isActive = item.id === value;
        const { Icon } = item;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onChange(item.id)}
            aria-pressed={isActive}
            className={`relative z-10 flex flex-1 items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
              isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
            {item.label}
            {item.count > 0 && (
              <span
                className={`rounded-full px-1.5 text-[10px] font-bold leading-4 ${
                  isActive
                    ? "bg-orange-primary text-white"
                    : "bg-internal-bg text-internal-text"
                }`}
              >
                {item.count > 99 ? "99+" : item.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
